import { useState, useEffect, useCallback } from 'react';
import ApiConfig   from './ApiConfig';
import { ChangePasswordForm } from './LoginPage';
import VideoList   from './VideoList';
import CreateVideo from './CreateVideo';
import EditVideo   from './EditVideo';
import DeleteVideo from './DeleteVideo';
import UserBadge   from './UserBadge';
import { useVideos } from '../hooks/useVideos';
import { useAuth }   from '../context/AuthContext';
import { colors } from './styles';

//const DEFAULT_BASE = 'https://affectionate-panini.192-250-231-23.plesk.page/api';
//const DEFAULT_BASE = 'http://208.109.39.244:5000/api';

const DEFAULT_BASE = 'https://manna-of-life.com/api';
//const DEFAULT_BASE = 'https://localhost:7247/api';

// Sidebar nav items
const NAV_ITEMS = [
  { id: 'videos', label: 'Videos', icon: '▶' },
  { id: 'unpublished', label: 'Unpublished Videos', icon: '🚫' },
  { id: 'settings', label: 'Settings', icon: '⚙' },
];

export default function VideosManager() {
  const { getToken, logout  } = useAuth();
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE);
  const [search, setSearch]   = useState('');
  const [activeNav, setActiveNav] = useState('videos');

  const [modal,         setModal]         = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [unpublishedVideos, setUnpublishedVideos] = useState([]);
  const [unpubLoading, setUnpubLoading] = useState(false);
  const [unpubError, setUnpubError] = useState(null);

  const { videos, loading, error, fetchVideos, createVideo, updateVideo, deleteVideo } =
    useVideos(baseUrl, getToken, logout);

  const fetchUnpublishedVideos = useCallback(async () => {
    setUnpubLoading(true);
    setUnpubError(null);
    try {
      const res = await fetch(`${baseUrl}/unpubvideos`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) throw new Error(`Failed to load unpublished videos (${res.status})`);
      const data = await res.json();
      setUnpublishedVideos(data);
    } catch (err) {
      setUnpubError(err.message);
    } finally {
      setUnpubLoading(false);
    }
  }, [baseUrl, getToken, logout]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  useEffect(() => {
    if (activeNav === 'unpublished') {
      fetchUnpublishedVideos();
    }
  }, [activeNav, fetchUnpublishedVideos]);

  const openCreate = ()  => setModal('create');
  const openEdit   = (v) => { setSelectedVideo(v); setModal('edit');   };
  const openDelete = (v) => { setSelectedVideo(v); setModal('delete'); };
  const closeModal = ()  => { setModal(null); setSelectedVideo(null);  };

  const handleCreate = async (body) => { await createVideo(body); };

  const handleUpdate = async (id, body) => {
    await updateVideo(id, body);
    if (activeNav === 'unpublished') fetchUnpublishedVideos();
  };

  const handleDelete = async (id) => {
    await deleteVideo(id);
    if (activeNav === 'unpublished') fetchUnpublishedVideos();
  };

  const handleBaseSet = (url) => { setBaseUrl(url); };

  const handleNavChange = (id) => {
    setActiveNav(id);
    setSearch('');
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: colors.bg }}>

      {/* ── Sidebar ── */}
      <aside style={{
          width: 230, flexShrink: 0,
          background: colors.sidebarBg,
          borderRight: `1px solid ${colors.sidebarBorder}`,
          display: 'flex', flexDirection: 'column',
          padding: '0',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}>
        {/* Logo / Brand */}
        <div style={{
          padding: '24px 20px 20px',
          borderBottom: `1px solid ${colors.sidebarBorder}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: `linear-gradient(135deg, ${colors.primary}, #8B5CF6)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 16,
            }}>▶</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: colors.text, letterSpacing: '-0.3px' }}>
                Mannah of Life
              </div>
              <div style={{ fontSize: 10, color: colors.textLight, letterSpacing: '0.3px' }}>
                Admin Portal
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ padding: '12px 10px', flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: colors.textLight, letterSpacing: '1px', textTransform: 'uppercase', padding: '0 8px', marginBottom: 6 }}>
            Library
          </div>
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className="nav-item"
              onClick={() => handleNavChange(item.id)}
              style={{
                width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8,
                border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                marginBottom: 2,
                background: activeNav === item.id ? colors.navActive : 'transparent',
                color: activeNav === item.id ? colors.navActiveText : colors.textMuted,
                transition: 'all 0.15s ease',
              }}
            >
              <span style={{ fontSize: 13, opacity: 0.8 }}>{item.icon}</span>
              {item.label}
              {item.id === 'videos' && (
                <span style={{
                  marginLeft: 'auto', fontSize: 11,
                  background: colors.navActive, color: colors.navActiveText,
                  borderRadius: 20, padding: '2px 7px', fontWeight: 600,
                }}>
                  {videos.length}
                </span>
              )}
              {item.id === 'unpublished' && unpublishedVideos.length > 0 && (
                <span style={{
                  marginLeft: 'auto', fontSize: 11,
                  background: colors.navActive, color: colors.navActiveText,
                  borderRadius: 20, padding: '2px 7px', fontWeight: 600,
                }}>
                  {unpublishedVideos.length}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* User badge in sidebar bottom */}
        <div style={{ borderTop: `1px solid ${colors.sidebarBorder}`, padding: '12px 10px' }}>
          <UserBadge />
        </div>
      </aside>

      {/* ── Main Content ── */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top header bar */}
        <header style={{
          padding: '16px 28px',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.bgSurface,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px', color: colors.text }}>
              {activeNav === 'videos'
                ? 'Video Library'
                : activeNav === 'unpublished'
                ? 'Unpublished Videos'
                : 'Settings'}
            </h1>
            <p style={{ fontSize: 12, color: colors.textLight, marginTop: 2 }}>
              {activeNav === 'videos'
                ? `Manage your video collection · ${videos.length} entries`
                : activeNav === 'unpublished'
                ? `Videos awaiting publish · ${unpublishedVideos.length} entries`
                : 'Configure your workspace'}
            </p>
          </div>
          {activeNav === 'videos' && (
            <button
              className="btn-hover-primary"
              onClick={openCreate}
              style={{
                padding: '10px 20px',
                background: `linear-gradient(135deg, ${colors.primary}, #7080F0)`,
                color: colors.white, border: 'none', fontWeight: 600,
                borderRadius: 10, fontSize: 14, cursor: 'pointer',
                boxShadow: '0 4px 20px rgba(91,110,232,0.4)',
                display: 'flex', alignItems: 'center', gap: 7,
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              Add Video
            </button>
          )}
        </header>

        {/* Page content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

          {activeNav === 'settings' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 600 }}>
              <div style={{ background: colors.bgCard, borderRadius: 14, border: `1px solid ${colors.border}`, padding: '20px 24px' }}>
                <h2 style={{ fontSize: 15, fontWeight: 600, color: colors.text, marginBottom: 4 }}>API Configuration</h2>
                <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>Set the base URL for the video API endpoint.</p>
                <ApiConfig baseUrl={baseUrl} onSet={handleBaseSet} />
              </div>
              <div style={{ background: colors.bgCard, borderRadius: 14, border: `1px solid ${colors.border}`, padding: '20px 24px' }}>
                <h2 style={{ fontSize: 15, fontWeight: 600, color: colors.text, marginBottom: 4 }}>Change Password</h2>
                <p style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>Update your account password.</p>
                <ChangePasswordForm baseUrl={baseUrl} token={getToken()} />
              </div>
            </div>
          )}

          {activeNav === 'videos' && (
            <>
              {error && (
                <div style={{
                  background: colors.dangerBg, color: colors.danger,
                  border: `1px solid ${colors.dangerBorder}`, borderRadius: 10,
                  padding: '12px 16px', fontSize: 13, marginBottom: '1.25rem',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>⚠</span> {error}
                </div>
              )}

              {loading ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 16, padding: '6rem 0',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    border: `3px solid ${colors.border}`,
                    borderTopColor: colors.primary,
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  <span style={{ color: colors.textMuted, fontSize: 14 }}>Loading videos...</span>
                </div>
              ) : (
                <VideoList
                  videos={videos}
                  search={search}
                  onSearchChange={setSearch}
                  onRefresh={fetchVideos}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onDelete={openDelete}
                />
              )}
            </>
          )}

          {activeNav === 'unpublished' && (
            <>
              {unpubError && (
                <div style={{
                  background: colors.dangerBg, color: colors.danger,
                  border: `1px solid ${colors.dangerBorder}`, borderRadius: 10,
                  padding: '12px 16px', fontSize: 13, marginBottom: '1.25rem',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>⚠</span> {unpubError}
                </div>
              )}

              {unpubLoading ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 16, padding: '6rem 0',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    border: `3px solid ${colors.border}`,
                    borderTopColor: colors.primary,
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  <span style={{ color: colors.textMuted, fontSize: 14 }}>Loading unpublished videos...</span>
                </div>
              ) : (
                <VideoList
                  videos={unpublishedVideos}
                  search={search}
                  onSearchChange={setSearch}
                  onRefresh={fetchUnpublishedVideos}
                  onCreate={openCreate}
                  onEdit={openEdit}
                  onDelete={openDelete}
                />
              )}
            </>
          )}

        </div>
      </main>

      {modal === 'create' && (
        <CreateVideo onSave={handleCreate} onClose={closeModal} />
      )}
      {modal === 'edit' && selectedVideo && (
        <EditVideo video={selectedVideo} onSave={handleUpdate} onClose={closeModal} />
      )}
      {modal === 'delete' && selectedVideo && (
        <DeleteVideo video={selectedVideo} onConfirm={handleDelete} onClose={closeModal} />
      )}
    </div>
  );
}
